import React from 'react';
import { Text, View } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { BlogPost } from '@/src/domain/models/BlogPost';

const DoubleEliminationContent: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      A double elimination bracket gives every competitor a second chance. Unlike single
      elimination — where one loss means you're out — double elimination requires two
      losses before a player is eliminated. It's the format of choice for serious
      esports events, fighting game tournaments, and any competition where organizers
      want results that reflect skill rather than a single bad performance.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      How double elimination works
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      A double elimination bracket is split into two parallel brackets that run
      simultaneously: the Winners Bracket and the Losers Bracket. Every player starts
      in the Winners Bracket. The first loss moves a player to the Losers Bracket.
      A second loss — anywhere, in either bracket — eliminates them from the tournament.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      The two brackets eventually converge at the Grand Final, where the Winners Bracket
      champion faces the Losers Bracket champion. The path through each bracket is very
      different: the Winners Bracket is a clean progression, while the Losers Bracket
      requires winning consecutive rounds to survive — a far more grueling path that
      is also more exciting to watch.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      The Winners Bracket
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      The Winners Bracket follows the same structure as a single elimination bracket —
      players are seeded, and the top half and bottom half of the bracket progress
      toward each other until one player remains undefeated. Players in the Winners
      Bracket have the advantage of knowing they can afford exactly one loss before
      the situation becomes critical.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      The Losers Bracket
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Every player who loses in the Winners Bracket drops into the Losers Bracket.
      The Losers Bracket also progresses through rounds, but it receives new players
      from the Winners Bracket at each stage. Players who win in the Losers Bracket
      continue; those who lose are eliminated. The Losers Bracket champion — the last
      player standing with exactly one total loss — advances to the Grand Final.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      The Grand Final and the reset
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      The Grand Final matches the undefeated Winners Bracket champion against the
      Losers Bracket champion (who has one loss). This asymmetry introduces the concept
      of the Grand Final reset:
    </Text>
    {[
      'If the Winners Bracket champion wins the Grand Final, the tournament ends — they are the winner, never having lost.',
      "If the Losers Bracket champion wins the Grand Final, both players now have exactly one loss. A second match (the reset) is played to determine the champion.",
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20, marginTop: 12 }}>
      The reset mechanic creates some of the most memorable moments in competitive gaming
      history — a player fighting back from the Losers Bracket to force a reset and
      win the whole tournament is the ultimate comeback story.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      How many matches in double elimination?
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      A double elimination bracket with N players requires between 2N−2 and 2N−1 matches
      (the extra match is the potential Grand Final reset). This is roughly twice as many
      matches as a single elimination bracket, which is why double elimination tournaments
      typically take longer and why they're best suited for events with enough time to
      complete all matches.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      When to use double elimination
    </Text>
    {[
      'Esports and gaming tournaments where a single technical issue or off-day should not define the result',
      'Fighting game events (Street Fighter, Smash Bros, Tekken) — the standard format for most major tournaments',
      'Any competition where you have the time to run the extra matches the format requires',
      'Events where you want to reward consistency and punish variance over pure luck',
      'Competitions with strong competitors who would otherwise be eliminated by a single bracket mismatch',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Double elimination in Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly handles the full complexity of double elimination automatically. Generate
      the bracket, and Tournaly creates both the Winners and Losers brackets, routes
      players between them as results come in, and triggers the Grand Final reset logic
      when applicable. The bracket view shows both brackets simultaneously so organizers
      and spectators can follow the entire tournament at a glance.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.textSecondary }}>
      Double Elimination is available on Tournaly Pro. Pro also includes PDF export
      so you can print or share the full bracket before the event.
    </Text>
  </View>
);

const DoubleEliminationContentEs: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Un bracket de doble eliminación da a cada competidor una segunda oportunidad. A
      diferencia de la eliminación simple — donde una derrota significa la eliminación —
      la doble eliminación requiere dos derrotas antes de ser eliminado. Es el formato
      elegido por los eventos de esports serios, los torneos de juegos de lucha y cualquier
      competición donde los organizadores quieren resultados que reflejen la habilidad
      más que una mala actuación puntual.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Cómo funciona la doble eliminación
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Un bracket de doble eliminación se divide en dos brackets paralelos que se ejecutan
      simultáneamente: el Bracket de Ganadores y el Bracket de Perdedores. Todos los
      jugadores empiezan en el Bracket de Ganadores. La primera derrota mueve a un jugador
      al Bracket de Perdedores. Una segunda derrota — en cualquier lugar, en cualquier
      bracket — lo elimina del torneo.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Los dos brackets convergen eventualmente en la Gran Final, donde el campeón del
      Bracket de Ganadores se enfrenta al campeón del Bracket de Perdedores. El camino a
      través de cada bracket es muy diferente: el Bracket de Ganadores es una progresión
      limpia, mientras que el Bracket de Perdedores requiere ganar rondas consecutivas
      para sobrevivir — un camino mucho más exigente que también es más emocionante de ver.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      La Gran Final y el reset
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      La Gran Final enfrenta al campeón invicto del Bracket de Ganadores contra el
      campeón del Bracket de Perdedores (que tiene una derrota). Esta asimetría introduce
      el concepto del reset de Gran Final:
    </Text>
    {[
      'Si el campeón del Bracket de Ganadores gana la Gran Final, el torneo termina — son el ganador, sin haber perdido nunca.',
      'Si el campeón del Bracket de Perdedores gana la Gran Final, ambos jugadores tienen exactamente una derrota. Se juega un segundo partido (el reset) para determinar al campeón.',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20, marginTop: 12 }}>
      El mecanismo del reset crea algunos de los momentos más memorables en la historia
      del gaming competitivo — un jugador que regresa desde el Bracket de Perdedores para
      forzar un reset y ganar todo el torneo es la historia de remontada definitiva.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      ¿Cuándo usar la doble eliminación?
    </Text>
    {[
      'Torneos de esports y gaming donde un problema técnico o un mal día no debería definir el resultado',
      'Eventos de juegos de lucha (Street Fighter, Smash Bros, Tekken) — el formato estándar para la mayoría de los torneos grandes',
      'Cualquier competición donde tienes tiempo para jugar los partidos extra que requiere el formato',
      'Eventos donde quieres recompensar la consistencia y penalizar la varianza sobre la pura suerte',
      'Competiciones con competidores fuertes que de otro modo serían eliminados por un único emparejamiento desfavorable',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Doble eliminación en Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly gestiona toda la complejidad de la doble eliminación automáticamente.
      Genera el bracket y Tournaly crea tanto el Bracket de Ganadores como el de Perdedores,
      mueve a los jugadores entre ellos conforme llegan los resultados, y activa la lógica
      del reset de Gran Final cuando corresponde. La vista de bracket muestra ambos brackets
      simultáneamente para que organizadores y espectadores puedan seguir todo el torneo
      de un vistazo.
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.textSecondary }}>
      La Doble Eliminación está disponible en Tournaly Pro. Pro también incluye exportación
      PDF para que puedas imprimir o compartir el bracket completo antes del evento.
    </Text>
  </View>
);

export const post: BlogPost = {
  slug: '2026-03-06-double-elimination-bracket-guide',
  title: 'Double Elimination Brackets: How They Work and When to Use Them',
  titleEs: 'Brackets de Doble Eliminación: Cómo Funcionan y Cuándo Usarlos',
  date: '2026-03-06',
  summary: 'A complete guide to double elimination brackets — Winners and Losers brackets explained, the Grand Final reset mechanic, match counts, and when double elimination is the right format for your tournament.',
  summaryEs: 'Una guía completa sobre los brackets de doble eliminación — brackets de Ganadores y Perdedores explicados, el mecanismo de reset de Gran Final, número de partidos y cuándo la doble eliminación es el formato adecuado para tu torneo.',
  tags: ['Guide', 'Formats', 'Double Elimination'],
  Content: DoubleEliminationContent,
  ContentEs: DoubleEliminationContentEs,
};
